import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import FeedItem from './FeedItem';
import ClipboardView from './ClipboardView';
import FeaturesView from './FeaturesView';
import ProfileView from './ProfileView';
import { feedItems } from '../data/feedItems';

const TabContent = memo(({ activeTab, highlightedClipId, isAddingClip, setIsAddingClip, onClipDetailOpenChange }) => {
  const [savedItemIds, setSavedItemIds] = useState(new Set());
  const [feedIndex, setFeedIndex] = useState(0);
  const feedScrollRef = useRef(null);

  const handleSaveItem = useCallback((itemId) => {
    setSavedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  useEffect(() => {
    const el = feedScrollRef.current;
    if (!el || feedItems.length === 0) return;

    const onScroll = () => {
      const slides = Array.from(el.children);
      if (slides.length === 0) return;

      const left = el.scrollLeft;
      let closestIndex = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      setFeedIndex(Math.min(closestIndex, feedItems.length - 1));
    };

    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [feedItems.length]);

  return (
    <>
      {activeTab === 'features' && (
        <div className="pb-4 w-full min-h-full">
          <FeaturesView />
        </div>
      )}
      
      {activeTab === 'feed' && (
        <div className="pb-4 w-full min-h-full" style={{ backgroundColor: '#efece4' }}>
          {feedItems.length > 0 ? (
            <div className="w-full px-4 pt-3">
              <header className="pb-2 flex items-center justify-end">
                <p className="text-[11px] font-semibold" style={{ letterSpacing: '1px', color: 'rgba(0,0,0,0.35)' }}>
                  {String(feedIndex + 1).padStart(2, '0')} / {String(feedItems.length).padStart(2, '0')}
                </p>
              </header>
              <div
                ref={feedScrollRef}
                className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth w-full"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {feedItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex-shrink-0 w-full min-w-full snap-start px-1"
                  >
                    <FeedItem
                      item={item}
                      onSave={handleSaveItem}
                      isSaved={savedItemIds.has(item.id)}
                      isActive={index === feedIndex}
                      layout="card"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 pt-4 pb-4">
                <button
                  onClick={() => {
                    const el = feedScrollRef.current;
                    if (el) el.scrollTo({ left: el.scrollLeft - el.clientWidth, behavior: 'smooth' });
                  }}
                  disabled={feedIndex === 0}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
                  style={{
                    background: feedIndex === 0 ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.07)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    color: feedIndex === 0 ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.55)',
                  }}
                  aria-label="Previous"
                >
                  ←
                </button>
                <button
                  onClick={() => {
                    const el = feedScrollRef.current;
                    if (el) el.scrollTo({ left: el.scrollLeft + el.clientWidth, behavior: 'smooth' });
                  }}
                  disabled={feedIndex === feedItems.length - 1}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
                  style={{
                    background: feedIndex === feedItems.length - 1 ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.07)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    color: feedIndex === feedItems.length - 1 ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.55)',
                  }}
                  aria-label="Next"
                >
                  →
                </button>
              </div>
            </div>
          ) : (
              <div className="flex flex-col items-center justify-center py-12 px-5 text-center">
                <div className="w-16 h-16 rounded-[12px] flex items-center justify-center mb-4" style={{ backgroundColor: '#F7F4EC', border: '1px solid rgba(0,0,0,0.08)' }}>
                  <span className="text-3xl">✨</span>
                </div>
                <h3 className="font-instrument text-[22px] text-[#111] mb-2">No items yet</h3>
                <p className="text-sm text-black/65 max-w-xs">
                  Your feed will appear here.
                </p>
              </div>
            )}
        </div>
      )}
      
      {activeTab === 'clipboard' && (
        <ClipboardView 
          highlightedClipId={highlightedClipId}
          isAddingClip={isAddingClip}
          setIsAddingClip={setIsAddingClip}
          onClipDetailOpenChange={onClipDetailOpenChange}
        />
      )}

      {activeTab === 'profile' && (
        <ProfileView />
      )}
    </>
  );
});

TabContent.displayName = 'TabContent';

TabContent.propTypes = {
  activeTab: PropTypes.oneOf(['features', 'feed', 'clipboard', 'profile']).isRequired,
  highlightedClipId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAddingClip: PropTypes.bool,
  setIsAddingClip: PropTypes.func,
  onClipDetailOpenChange: PropTypes.func,
};

export default TabContent;
